import useFormattedDate from "@/dates/use-formatted-date";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import {
  Article,
  ArticleChildren,
  ArticleImage,
  ArticleImagesStack,
  ArticleList,
  ArticlesGroup,
  ArticlesGroupAction,
  ArticlesGroupContent,
  ArticlesGroupFooter,
  ArticlesGroupSubtitle,
  ArticlesGroupTitle,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useReturnRequest } from "@babackapp/return-widget-components/queries/use-return-requests";
import { TruckIcon } from "@heroicons/react/24/solid";
import { ArrowLeft, ChevronDown, Download } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { useLineItemImages } from "../../hooks/use-line-item-images";

export function ReturnRequestDetails() {
  const { orderId, shopSlug, returnRequestId, logoUrl } = useWidgetParams();
  const { data: returnRequest } = useReturnRequest({
    orderId,
    shopSlug,
    returnRequestId: returnRequestId!,
  });
  // const createdAt = useFormattedDate(returnRequest.createdAt);

  return (
    <WidgetPage>
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>
          <Link to="..">
            <Button variant="ghost">
              <ArrowLeft />
            </Button>
          </Link>
          Détails de votre retour
        </HeaderTitle>
        <HeaderDescription>
          Consultez et gérez votre retour. Nous sommes là pour vous aider à
          chaque étape du processus de retour.
        </HeaderDescription>
      </Header>

      <ReturnContent />

      {/*<motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Alert variant="pending">
          <TruckIcon />
          <AlertTitle>En attente de retrait par le transporteur</AlertTitle>
          <AlertDescription>
            Nous avons traité et validé votre demande de retour.
            <Button
              variant="outline"
              size="sm"
              className="flex mt-4 text-foreground"
            >
              <Download /> Télécharger l'étiquette
            </Button>
          </AlertDescription>
        </Alert>
      </motion.div>*/}
    </WidgetPage>
  );
}

function ReturnContentExpanded() {
  const { orderId, shopSlug, returnRequestId } = useWidgetParams();
  const { data: returnRequest } = useReturnRequest({
    orderId,
    shopSlug,
    returnRequestId: returnRequestId!,
  });
  const lineItems = useLineItemImages(returnRequest);
  const createdAt = useFormattedDate(returnRequest.createdAt);

  return (
    <ArticlesGroup
      layoutId="return-content"
      variants={{}}
      className="overflow-hidden"
    >
      <ArticlesGroupContent
        disableVariants={{ enter: true, exit: true, visible: true }}
        className="border-b-transparent pt-0"
      >
        <ArticlesGroupTitle layoutId={returnRequest.id + "-title"}>
          Retour du {createdAt.formattedDate}
        </ArticlesGroupTitle>
        <ArticlesGroupSubtitle
          layoutId={returnRequest.id + "-subtitle"}
          className="max-w-[340px]"
        >
          {lineItems.length} article(s)
        </ArticlesGroupSubtitle>
        <ArticlesGroupFooter
          initial={{ opacity: 0, x: -10 }}
          animate={{
            opacity: 1,
            x: 0,
            transition: { delay: 0.3, ease: [0.4, 0, 0.2, 1] },
          }}
          exit={{ opacity: 0, x: 10 }}
        ></ArticlesGroupFooter>
        <ArticlesGroupAction layoutId="action">
          <motion.span
            layoutId="action-rotate"
            animate={{ rotate: 180 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown />
          </motion.span>
        </ArticlesGroupAction>
        <ArticleChildren>
          <ArticleList size="sm" variants={{}}>
            {lineItems.map((li) => (
              <Article key={li.line_item_id} variants={{}}>
                <ArticleImage
                  layoutId={`${returnRequest.id}${li.line_item_id}${li.image!}`}
                  src={li.image!}
                />
                <ArticleTitle>{li.title}</ArticleTitle>
                <ArticleSubtitle>
                  {li.quantity} × {li.price}€
                </ArticleSubtitle>
              </Article>
            ))}
          </ArticleList>
        </ArticleChildren>
      </ArticlesGroupContent>
    </ArticlesGroup>
  );
}

function ReturnContentCollapsed() {
  const { orderId, shopSlug, returnRequestId } = useWidgetParams();
  const { data: returnRequest } = useReturnRequest({
    orderId,
    shopSlug,
    returnRequestId: returnRequestId!,
  });
  const lineItems = useLineItemImages(returnRequest);
  const createdAt = useFormattedDate(returnRequest.createdAt);

  return (
    <ArticlesGroup layoutId="return-content" variants={{}}>
      <ArticlesGroupContent
        disableVariants={{ enter: true, exit: true, visible: true }}
      >
        <ArticlesGroupTitle layoutId={returnRequest.id + "-title"}>
          Retour du {createdAt.formattedDate}
        </ArticlesGroupTitle>
        <ArticlesGroupSubtitle
          layoutId={returnRequest.id + "-subtitle"}
          className="max-w-[340px]"
        >
          {lineItems.length} article(s)
        </ArticlesGroupSubtitle>
        <ArticlesGroupFooter
          initial={{ opacity: 0, x: -10 }}
          animate={{
            opacity: 1,
            x: 0,
            transition: { delay: 0.3, ease: [0.4, 0, 0.2, 1] },
          }}
          exit={{ opacity: 0, x: 10 }}
        ></ArticlesGroupFooter>
        <ArticleImagesStack
          grid
          layoutIdPrefix={returnRequest.id}
          srcs={lineItems.map((li) => li.image!).filter(Boolean)}
          ids={lineItems.map((li) => li.line_item_id)}
        />
        <ArticlesGroupAction layoutId="action">
          <motion.span
            layoutId="action-rotate"
            animate={{ rotate: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown />
          </motion.span>
        </ArticlesGroupAction>
      </ArticlesGroupContent>
    </ArticlesGroup>
  );
}

export function ReturnContent() {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      onClick={() => setExpanded((e) => !e)}
      className="cursor-pointer"
    >
      {expanded ? <ReturnContentExpanded /> : <ReturnContentCollapsed />}
    </motion.div>
  );
}
